import { formatGuestCount, leadGuests } from "./guestCount";

export const LEAD_STATUS_LABEL = {
  new: "New", contacted: "Contacted", tour_scheduled: "Tour scheduled", toured: "Toured",
  nurture: "Nurture", converted: "Converted", lost: "Lost", no_contact: "No contact", dnc: "DNC",
} as const;

export type LeadCardLines = { line1: string; line2: string; line3: string; status: string };
type CardLead = {
  status?: string; bookingStatus?: string | null; eventName?: string | null; eventTypeRaw?: string | null; eventDate?: string | null;
  eventDateFlexible?: number | boolean; guestCount?: number | null; guestCountMin?: number | null; guestCountMax?: number | null;
};
type CardContact = { firstName?: string | null; lastName?: string | null; name?: string | null; companyName?: string | null; email?: string | null; phone?: string | null };
const clean = (value: string | null | undefined) => value?.trim() ?? "";

export function cardIdentity(name?: string | null, company?: string | null, fallback = "No name") {
  return [...new Set([clean(name), clean(company)].filter(Boolean))].join(" · ") || fallback;
}
export function cardEventName(event?: string | null, identity?: string | null) {
  const name = clean(event);
  return name.toLocaleLowerCase() === clean(identity).toLocaleLowerCase() ? "" : name;
}
const BOOKING_STATUS_LABEL: Record<string, string> = { pending: "Pending", contract_sent: "Contract sent", signed: "Signed", booked: "Booked", completed: "Completed", cancelled: "Cancelled" };

/** SPEC §6.1: the same three lines in records, lists, tours, Today and staff notifications. */
export function leadCardLines(lead: CardLead, contact: CardContact, venue?: { name?: string | null } | null, eventType?: { name?: string | null } | null): LeadCardLines {
  const name = clean(contact.name) || [clean(contact.firstName), clean(contact.lastName)].filter(Boolean).join(" ");
  const identity = cardIdentity(name, contact.companyName, clean(contact.email) || clean(contact.phone) || "No name");
  const type = clean(lead.eventTypeRaw) || clean(eventType?.name);
  const line2 = cardEventName(clean(lead.eventName) || type, identity);
  let date = "No date";
  if (lead.eventDateFlexible) date = "Flexible";
  else if (lead.eventDate) {
    const parsed = new Date(`${lead.eventDate}T12:00:00Z`);
    date = Number.isNaN(parsed.getTime()) ? lead.eventDate : new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" }).format(parsed);
  }
  const guests = leadGuests(lead);
  return {
    line1: identity,
    line2,
    line3: [clean(venue?.name) || "No venue", date, guests && formatGuestCount(guests.min, guests.max), type.toLowerCase() !== line2.toLowerCase() ? type : null].filter(Boolean).join(" · "),
    status: lead.bookingStatus ? (BOOKING_STATUS_LABEL[lead.bookingStatus] ?? lead.bookingStatus) : LEAD_STATUS_LABEL[lead.status as keyof typeof LEAD_STATUS_LABEL] ?? lead.status ?? "New",
  };
}

export type LeadCardRow = CardLead & {
  contactName?: string | null; contactCompanyName?: string | null; contactEmail?: string | null; contactPhone?: string | null;
  venueName?: string | null; venueShortName?: string | null; eventTypeName?: string | null;
};
export const leadCardForRow = (row: LeadCardRow): LeadCardLines => leadCardLines(row,
  { name: row.contactName, companyName: row.contactCompanyName, email: row.contactEmail, phone: row.contactPhone },
  { name: row.venueName ?? row.venueShortName }, { name: row.eventTypeName });
